import styled from '@emotion/styled';

// components
import CardTemplates from '@components/_molecules/CardTemplates';
import TextInputBox from '@components/_molecules/TextInputBox';
import FileInputBox from '@components/_molecules/FileInputBox';
import TextAreaBox from '@components/_molecules/TextAreaBox';
import Typography from '@components/_atoms/Typography';
import DaysInputBox from '../DaysInputBox';

// constants
import { TABLET_MODE } from '@constants/size';
import { TypoVariant } from '@constants/atoms';
import { gray } from '@styles/Colors';
import { useIsMobile } from '@hooks/responsive/useIsMobile';
import { FileTarget } from '@constants/service';

// type
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { returnOnlyNumber } from '@utils/regex';

type Props = {
  quotationForm: QuotationFormType;
  setQuotationForm: Dispatch<SetStateAction<QuotationFormType>>;
};

export type QuotationFormType = {
  readonly [key: string]: string | boolean | undefined;
  readonly req_close_days?: string;
  readonly req_delivery_days?: string;
  readonly shipping_area?: string;
  readonly terms_of_payment?: string;
  readonly additional_request_file?: string;
  readonly additional_request_file_name?: string;
  readonly seller_note?: string;
};

const REQ_MAX_DAYS = 999;

const FormWrapper = styled.div`
  display: flex;
  column-gap: 24px;

  @media ${TABLET_MODE} {
    flex-direction: column;
    row-gap: 24px;
  }
`;

const FormBox = styled.div`
  flex: 1;

  display: grid;
  row-gap: 24px;
`;

const PaymentsWrapper = styled.div``;

const CheckBoxWrapper = styled.div`
  margin: 8px 0 4px;
`;

const MemoWrapper = styled.div`
  display: grid;
  row-gap: 8px;
`;

const MemoText = styled.div`
  @media ${TABLET_MODE} {
    text-align: right;
  }
`;

export default function QuotationFormCard({ quotationForm, setQuotationForm }: Props) {
  const { t } = useTranslation('component');
  const isMobile = useIsMobile();
  const [sellerNoteCount, setSellerNoteCount] = useState(0);

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setQuotationForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = returnOnlyNumber(value);
    if (numValue > REQ_MAX_DAYS) return;
    setQuotationForm((prev) => ({ ...prev, [name]: `${numValue || ''}` }));
  };

  const handleFileUpload = (file: File, cdnUrl: string, name: string) => {
    const { name: fileName } = file;

    const updatedName = `${name}_name`;
    setQuotationForm((prev) => ({ ...prev, [name]: cdnUrl, [updatedName]: fileName }));
  };

  const handleFileRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    const { name } = e.target as HTMLButtonElement;
    setQuotationForm((prev) => ({ ...prev, [name]: '' }));
  };

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setQuotationForm((prev) => ({ ...prev, [name]: value }));
    setSellerNoteCount(value.length);
  };

  return (
    <CardTemplates title={t('quotationForm.title')}>
      <FormWrapper>
        <FormBox>
          <DaysInputBox
            title={t('quotationForm.reqCloseDays')}
            textInput={{
              name: 'req_close_days',
              value: quotationForm.req_close_days,
              onChange: handleDateChange,
              placeholder: 'ex) 20',
              suffixText: t('quotationForm.reqCloseDaysSuffix'),
              style: { width: 120 },
            }}
            isRequired
          />

          <DaysInputBox
            title={t('quotationForm.reqDeliveryDays')}
            textInput={{
              name: 'req_delivery_days',
              value: quotationForm.req_delivery_days,
              onChange: handleDateChange,
              placeholder: 'ex) 20',
              suffixText: t('quotationForm.reqDeliveryDaysSuffix'),
              style: { width: 120 },
            }}
            isRequired
          />

          <TextInputBox
            title={t('quotationForm.isLoading')}
            textInput={{
              name: 'shipping_area',
              value: quotationForm.shipping_area,
              onChange: handleFormChange,
              placeholder: t('quotationForm.isLoading-placeholder'),
            }}
            isRequired
          />
          <PaymentsWrapper>
            <TextInputBox
              title={t('quotationForm.termsOfPayment')}
              textInput={{
                name: 'terms_of_payment',
                value: quotationForm.terms_of_payment,
                onChange: handleFormChange,
                placeholder: t('quotationForm.termsOfPayment-placeholder'),
              }}
              isRequired
            />
          </PaymentsWrapper>
        </FormBox>
        <FormBox>
          <FileInputBox
            title={t('quotationForm.additionalRequestFile')}
            fileInput={{
              cdnKey: quotationForm.additional_request_file,
              name: 'additional_request_file',
              formFileName: quotationForm.additional_request_file_name,
              target: FileTarget.AR,
              handleFileUpload,
              handleFileRemove,
            }}
          />
          <MemoWrapper>
            <TextAreaBox
              title={t('quotationForm.sellerNote')}
              textarea={{
                height: '110px',
                name: 'seller_note',
                value: quotationForm.seller_note,
                placeholder: t('quotationForm.sellerNote-placeholder'),
                onChange: handleTextAreaChange,
              }}
            />
            <MemoText>
              <Typography variant={TypoVariant.B4} color={gray.gray7}>
                {!isMobile && <>{t('quotationForm.count-description')}</>} ({sellerNoteCount} / 1,000)
              </Typography>
            </MemoText>
          </MemoWrapper>
        </FormBox>
      </FormWrapper>
    </CardTemplates>
  );
}
